import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {DataSetConditionalDimensionsState} from './dataSetConditionalDimensions';
import {
  createDataSetConditionalDimensionSuccess,
  deleteDataSetConditionalDimensionSuccess,
  initializeDataSetConditionalDimension,
  initializeDataSetConditionalDimensionSuccess,
  loadDataSetConditionalDimensions,
  loadDataSetConditionalDimensionsSuccess,
  updateDataSetConditionalDimensions,
  updateDataSetConditionalDimensionsSuccess,
  loadPublicDataSetConditionalDimensions,
  loadPublicDataSetConditionalDimensionsSuccess,
  updateDataSetConditionalDimensionSuccess
} from './dataSetConditionalDimensions.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, Serializers} from '../../interfaces/serializer';
import {DataSetConditionalDimension} from '../../interfaces/dimensions';
import {Sort} from '../../libraries/sort';
import {
  DatasetConditionalDimensionComponentObject,
  DatasetConditionalDimensionComponentObjects
} from '../../classes/dataset-conditional-dimension-component-object';
import {DimensionComponentObject, DimensionComponentObjects} from '../../classes/dimension-component-object';

const _reducer: ActionReducer<DataSetConditionalDimensionsState> = createReducer(
  { serialized: [], componentObjects: [], loading: false, loaded: false, initialized: false },
  on(loadDataSetConditionalDimensions, (state: DataSetConditionalDimensionsState): DataSetConditionalDimensionsState => {
    return {
      ...state,
      loading: true,
      loaded: false,
      initialized: false
    };
  }),
  on(loadDataSetConditionalDimensionsSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }): DataSetConditionalDimensionsState => {
    return {
      ...state,
      serialized: action.dimensions,
      componentObjects: _sort(action.dimensions.map((dimension: Serializer<DataSetConditionalDimension>): DatasetConditionalDimensionComponentObject => new DatasetConditionalDimensionComponentObject(dimension))),
      loading: false,
      loaded: true
    };
  }),
  on(loadPublicDataSetConditionalDimensions, (state: DataSetConditionalDimensionsState): DataSetConditionalDimensionsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(loadPublicDataSetConditionalDimensionsSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }): DataSetConditionalDimensionsState => {
    return {
      ...state,
      serialized: action.dimensions,
      componentObjects: action.dimensions.map((dimension: Serializer<DataSetConditionalDimension>): DatasetConditionalDimensionComponentObject => new DatasetConditionalDimensionComponentObject(dimension)),
      loading: false,
      loaded: true
    };
  }),
  on(updateDataSetConditionalDimensions, (state: DataSetConditionalDimensionsState): DataSetConditionalDimensionsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(updateDataSetConditionalDimensionsSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }): DataSetConditionalDimensionsState => {
    action.dimensions.forEach((dimension: Serializer<DataSetConditionalDimension>): void => {
      const componentObject: DatasetConditionalDimensionComponentObject = state.componentObjects.find((componentObject: DimensionComponentObject): boolean => componentObject.payload.id === dimension.id);

      if (componentObject) {
        componentObject.update(dimension)
      } else {
        state.componentObjects.push(new DatasetConditionalDimensionComponentObject(dimension));
      }
    });

    state.componentObjects = state.componentObjects.filter((dimension: DimensionComponentObject): boolean => action.dimensions.includes(dimension.payload as Serializer<DataSetConditionalDimension>));

    return {
      ...state,
      serialized: action.dimensions,
      componentObjects: _sort(state.componentObjects),
      loading: false,
      loaded: true
    };
  }),
  on(createDataSetConditionalDimensionSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }): DataSetConditionalDimensionsState => {
    return {
      ...state,
      serialized: [
        ...state.serialized,
        action.dimension
      ],
      componentObjects: _sort([
        ...state.componentObjects,
        new DatasetConditionalDimensionComponentObject(action.dimension)
      ])
    };
  }),
  on(updateDataSetConditionalDimensionSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }): DataSetConditionalDimensionsState => {
    state.componentObjects.find((dimension: DatasetConditionalDimensionComponentObject): boolean => dimension.payload.id === action.dimension.id).update(action.dimension);

    return {
      ...state,
      serialized: state.serialized.map((dimension: Serializer<DataSetConditionalDimension>): Serializer<DataSetConditionalDimension> => dimension.id === action.dimension.id && action.dimension || dimension)
    };
  }),
  on(deleteDataSetConditionalDimensionSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }): DataSetConditionalDimensionsState => {
    return {
      ...state,
      serialized: state.serialized.filter((dimension: Serializer<DataSetConditionalDimension>): boolean => dimension.id !== action.dimension.id),
      componentObjects: state.componentObjects.filter((dimension: DatasetConditionalDimensionComponentObject): boolean => dimension.payload.id !== action.dimension.id)
    };
  }),
  on(initializeDataSetConditionalDimension, (state: DataSetConditionalDimensionsState): DataSetConditionalDimensionsState => {
    return {
      ...state,
      initialized: false
    };
  }),
  on(initializeDataSetConditionalDimensionSuccess, (state: DataSetConditionalDimensionsState, action: TypedAction<string> & { dimensions: DimensionComponentObjects, datasourceDimensions: DimensionComponentObjects, generalDimensions: DimensionComponentObjects }): DataSetConditionalDimensionsState => {
    state.componentObjects.forEach((dimension: DatasetConditionalDimensionComponentObject): void => {
      dimension.init([...action.dimensions, ...action.generalDimensions], action.datasourceDimensions);
    });

    return {
      ...state,
      initialized: true
    };
  }),
);

export const dataSetConditionalDimensionsReducer: ActionReducer<DataSetConditionalDimensionsState> = (state: DataSetConditionalDimensionsState, action: Action) => {
  return _reducer(state, action);
};

function _sort(dimensions: DatasetConditionalDimensionComponentObjects): DatasetConditionalDimensionComponentObjects {
  return dimensions.sort((a: DatasetConditionalDimensionComponentObject, b: DatasetConditionalDimensionComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
}
